<script setup>
import { computed } from "vue"
import { RouterLink } from "vue-router"
import { cn } from "@/lib/utils"

const props = defineProps({
  variant: { type: String, default: "default" }, // default | outline | ghost | secondary
  size:    { type: String, default: "default" }, // default | sm | lg | icon
  class:   { type: null, default: "" },
  // Render as a router link styled as a button. A <button> inside a <RouterLink>
  // is invalid HTML and gives keyboard users two tab stops for one action.
  to:      { type: [String, Object], default: null },
})

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium " +
  "ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 " +
  "active:scale-[.98]"

const variants = {
  default:   "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  outline:   "border border-input bg-card hover:bg-muted hover:text-foreground",
  ghost:     "hover:bg-muted hover:text-foreground",
}
const sizes = {
  default: "h-10 px-5 py-2",
  sm:      "h-9 px-3.5 text-xs",
  lg:      "h-12 px-8 text-base",
  icon:    "h-10 w-10",
}

const classes = computed(() =>
  cn(base, variants[props.variant] || variants.default, sizes[props.size] || sizes.default, props.class)
)
</script>

<template>
  <RouterLink v-if="to" :to="to" :class="classes"><slot /></RouterLink>
  <!-- type="button" unless the caller passes type="submit": a Button placed in a
       form without one would otherwise submit it. -->
  <button v-else type="button" :class="classes"><slot /></button>
</template>
